import * as PropTypes from 'prop-types';
import * as React from 'react';

import styles from './Fotorama.css';
import { IFotoramaAPI, IFotoramaData } from '../../../typings/fotorama';
import { FullScreenControlsContainer } from '../../containers/FullScreenControls';
import { loadFotorama } from '../../utils/loadFotorama';
import { logComponentError } from '../../utils/log_component_error';

export interface IFotoramaStoreProps {
  slides: IFotoramaData[];
  showElementId?: number;
  fotoramaProps?: {
    'data-width'?: number | string | null;
    'data-minwidth'?: number | string | null;
    'data-maxwidth'?: number | string | null;
    'data-height'?: number | string | null;
    'data-minheight'?: number | string | null;
    'data-margin'?: number;
    'data-ratio'?: number | string;
    'data-glimpse'?: number | string;
    'data-nav'?: 'dots' | 'thumbs' | false;
    'data-navposition'?: number | string;
    'data-navwidth'?: string;
    'data-thumbwidth'?: number;
    'data-thumbheight'?: number;
    'data-thumbmargin'?: number;
    'data-thumbborderwidth'?: number;
    'data-allowfullscreen'?: boolean | 'native';
    'data-fit'?: 'contain' | 'cover' | 'scaledown' | 'none';
    'data-thumbfit'?: 'contain' | 'cover' | 'scaledown' | 'none';
    'data-transition'?: 'slide' | 'crossfade' | 'dissolve';
    'data-clicktransition'?: string;
    'data-transitionduration'?: number;
    'data-captions'?: boolean;
    'data-hash'?: boolean;
    'data-startindex'?: number | string;
    'data-loop'?: boolean;
    'data-autoplay'?: boolean | number;
    'data-stopautoplayontouch'?: boolean;
    'data-keyboard'?: boolean | object;
    'data-arrows'?: boolean | 'always';
    'data-click'?: boolean;
    'data-swipe'?: boolean;
    'data-trackpad'?: boolean;
    'data-shuffle'?: boolean;
    'data-direction'?: 'rtl' | 'ltr';
    'data-spinner'?: object;
    'data-shadows'?: boolean;
  };
  fullfit?: 'contain' | 'cover' | 'scaledown' | 'none';
}

export interface IFotoramaDispatchProps {
  onInitialized(): void;
  onInit?(): void;
  onPlayVideo(): void;
  onStopVideo(): void;
  onElementShowed(): void;
  onFullScreenToggle(opened: boolean): void;
  onSlide?(e: Event, fotorama: IFotoramaAPI): void;
}

export interface IFotoramaState {
  isFullScreen: boolean;
}

export interface IFotoramaOwnProps {
  isFullScreen?: boolean;
  carouselElementId?: number;
  onExitFullScreen?(): void;
  onSlideChanged?(e: Event, fotorama: IFotoramaAPI): void;
}

export type IFotoramaProps = IFotoramaStoreProps & IFotoramaDispatchProps & IFotoramaOwnProps;

export class Fotorama extends React.Component<IFotoramaProps, IFotoramaState> {
  public static contextTypes = {
    reactErrorLogger: PropTypes.object,
  };
  private fotoramaRef: HTMLDivElement | null;
  private $fotorama: {
    on(type: string, cb: (e: Event, fotorama: IFotoramaAPI) => void): void;
    data(name: string): IFotoramaAPI;
    unbind(): void;
    show(id: number): void;
  };
  private fotorama: IFotoramaAPI;
  // @ts-expect-error
  // oxlint-disable-next-line typescript/no-explicit-any
  public context: any;
  public state = { isFullScreen: false };

  public async componentDidMount() {
    await loadFotorama();

    if (window.$ && this.fotoramaRef) {
      this.initFotorama();

      if (this.props.isFullScreen) {
        this.fotorama.requestFullScreen();
        const carouselElementId = this.props.carouselElementId;

        if (carouselElementId) {
          this.fotorama.show(carouselElementId);
          this.props.onElementShowed();
        }
      }
    }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logComponentError({
      error,
      errorInfo,
      logger: this.context.reactErrorLogger,
    });
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IFotoramaProps) {
    // move gallery to specific element
    if (nextProps.showElementId !== undefined) {
      this.fotorama.show(nextProps.showElementId);
      this.props.onElementShowed();
    }
  }

  public componentWillUnmount() {
    this.$fotorama.unbind();
  }

  private onExitFullScreen = () => this.fotorama.cancelFullScreen();

  private onPlayVideo = () => this.fotorama.playVideo();

  public render() {
    const { slides, fotoramaProps } = this.props;

    return slides.length ? (
      <div>
        {this.state.isFullScreen && <FullScreenControlsContainer exitFullscreen={this.onExitFullScreen} />}
        <div>
          <div
            className={styles['fotorama']}
            data-width="100%"
            data-height="442"
            data-nav="thumbs"
            data-fit="contain"
            data-keyboard="true"
            data-loop="true"
            data-arrows="true"
            data-swipe="false"
            data-thumbmargin="5"
            data-auto="false"
            data-allowfullscreen="true"
            ref={fotorama => (this.fotoramaRef = fotorama)}
            {...(fotoramaProps ? fotoramaProps : {})}
          />
        </div>
      </div>
    ) : (
      <div className={styles['default_content']} />
    );
  }

  private onFullScreenEnter = () => {
    const { fullfit, onFullScreenToggle } = this.props;

    if (fullfit) {
      this.fotorama.setOptions({ fit: fullfit });
    }

    this.setState({ isFullScreen: true }, this.onPlayVideo);
    onFullScreenToggle(true);
  };

  private onFullScreenExit = () => {
    const { fullfit, fotoramaProps, onFullScreenToggle, onExitFullScreen } = this.props;

    if (fullfit) {
      this.fotorama.setOptions({ fit: (fotoramaProps && fotoramaProps['data-fit']) || 'contain' });
    }

    this.setState({ isFullScreen: false });
    onFullScreenToggle(false);
    if (onExitFullScreen) {
      onExitFullScreen();
    }
  };

  private onInitialized = () => {
    const { onInitialized, onInit } = this.props;
    // For redux
    onInitialized();
    // Optional callback
    if (onInit) {
      onInit();
    }
  };

  private initFotorama() {
    const { slides, onSlide, onSlideChanged } = this.props;

    // init fotorama
    this.$fotorama = window.$(this.fotoramaRef).fotorama({
      data: slides as IFotoramaData[],
      startindex: 0,
    });

    this.fotorama = this.$fotorama.data('fotorama');

    // add event listeners
    this.$fotorama.on('fotorama:load', this.onInitialized);

    this.$fotorama.on('fotorama:loadvideo', this.props.onPlayVideo);

    this.$fotorama.on('fotorama:unloadvideo', this.props.onStopVideo);

    this.$fotorama.on('fotorama:fullscreenenter', this.onFullScreenEnter);

    this.$fotorama.on('fotorama:fullscreenexit', this.onFullScreenExit);

    if (onSlide) {
      this.$fotorama.on('fotorama:show', onSlide);
    }

    if (onSlideChanged) {
      this.$fotorama.on('fotorama:showend', onSlideChanged);
    }
  }
}
